import React, { useEffect, useRef, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import andrewNgImage from "../images/Andrew_Ng.jpeg";
import chaoPengImage from "../images/Chao.jpeg";
import dwarakImage from "../images/Dwarak.jpeg";
import eliImage from "../images/Eli.jpeg";
import jasonImage from "../images/Jason.jpeg";
import micheleImage from "../images/michele.jpeg";
import oferImage from "../images/ofer.jpeg";
import paxtonImage from "../images/paxton.jpeg";
import tengyuImage from "../images/Tengyu.jpg";
import yaadImage from "../images/Yaad.png";

const ANIMATION_CONFIG = {
  SMOOTH_DURATION: 600,
  INITIAL_DURATION: 1500,
  INITIAL_X_OFFSET: 70,
  INITIAL_Y_OFFSET: 60,
};

const clamp = (value, min = 0, max = 100) =>
  Math.min(Math.max(value, min), max);

const round = (value, precision = 3) => parseFloat(value.toFixed(precision));

const adjust = (value, fromMin, fromMax, toMin, toMax) =>
  round(toMin + ((toMax - toMin) * (value - fromMin)) / (fromMax - fromMin));

const easeInOutCubic = (x) =>
  x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

const Speakers = () => {
  const speakers = [
    {
      id: 1,
      name: "Andrew Ng",
      title: "Managing General Partner, AI Fund",
      image: andrewNgImage,
      bio: "Andrew Ng is the Managing General Partner at AI Fund, Founder of DeepLearning.AI, Executive Chairman of LandingAI, and an Adjunct Professor at Stanford University.",
      socials: {
        twitter: "https://twitter.com/AndrewYNg",
        linkedin: "https://linkedin.com/in/andrewyng",
        website: "https://andrewng.org",
      },
    },
    {
      id: 2,
      name: "Chao Peng",
      title: "Principal Research Scientist, Trae",
      image: chaoPengImage,
      bio: "Chao Peng leads the Software Engineering Lab at Trae, focusing on AI agents for software engineering. He holds a PhD in Informatics from University of Edinburgh.",
      socials: {
        linkedin: "https://www.linkedin.com/in/chao-peng-uoe/",
        website: "https://chao-peng.github.io/",
      },
    },
    {
      id: 3,
      name: "Dwarak Rajagopal",
      title: "VP of AI Engineering, Snowflake",
      image: dwarakImage,
      bio: "Dwarak Rajagopal is VP and Head of AI Engineering at Snowflake, with extensive experience leading AI/ML teams at Google, Meta, Uber, Apple, and AMD.",
      socials: {
        twitter: "https://x.com/dwarak",
        linkedin: "https://www.linkedin.com/in/dwarakrajagopal/",
      },
    },
    {
      id: 4,
      name: "Eli Chen",
      title: "Principal Technical Architect, AI Fund",
      image: eliImage,
      bio: "Eli Chen is a startup veteran who worked at Netflix and Twitter during their early growth, co-founded AI companies including Credo AI, and now helps AI Fund startups build products.",
      socials: {
        twitter: "https://x.com/elichen/",
        linkedin: "https://linkedin.com/in/elichen/",
        website: "https://elichen.github.io/",
      },
    },
    {
      id: 5,
      name: "Jason Koo",
      title: "Developer Advocate, Neo4j",
      image: jasonImage,
      bio: "Jason Koo is a Developer Relations specialist at Neo4j, building developer communities and creating educational content for graph databases and emerging technologies.",
      socials: {
        linkedin: "https://www.linkedin.com/in/jason-koo-usa/",
      },
    },
    {
      id: 6,
      name: "Michele Catasta",
      title: "President, Replit",
      image: micheleImage,
      bio: "Michele Catasta is an AI researcher specializing in Large Language Models and AI for code. Stanford alumnus who leads AI development and strategic initiatives at Replit.",
      socials: {
        twitter: "https://x.com/pirroh/",
        linkedin: "https://www.linkedin.com/in/pirroh/",
        website: "https://github.com/pirroh",
      },
    },
    {
      id: 7,
      name: "Ofer Mendelevitch",
      title: "Head of Developer Relations, Vectara",
      image: oferImage,
      bio: "Ofer Mendelevitch is a data science veteran who built machine learning systems at Yahoo!, co-founded synthetic data company Syntegra, and now leads developer relations at Vectara.",
      socials: {
        twitter: "https://x.com/ofermend",
        linkedin: "https://www.linkedin.com/in/ofermend/",
      },
    },
    {
      id: 8,
      name: "Paxton Maeder-York",
      title: "Venture Partnerships, Anthropic",
      image: paxtonImage,
      bio: "Paxton Maeder-York is a medical tech innovator who built products at surgical robotics pioneer Auris Health, founded AI-powered fertility company Alife, and now helps Anthropic expand partnerships in the AI ecosystem.",
      socials: {
        twitter: "https://x.com/pmaederyork",
        linkedin: "https://linkedin.com/in/paxton-maeder-york-00108736/",
      },
    },
    {
      id: 9,
      name: "Tengyu Ma",
      title: "Chief AI Scientist, MongoDB",
      image: tengyuImage,
      bio: "Tengyu Ma was CEO & Co-founder of Voyage AI, acquired by MongoDB, where he is now Chief AI Scientist. He's also an Assistant Professor at Stanford, specializing in ML and LLMs.",
      socials: {
        twitter: "https://x.com/tengyuma",
        linkedin: "https://www.linkedin.com/in/tengyuma/",
        website: "https://ai.stanford.edu/~tengyuma/",
      },
    },
    {
      id: 10,
      name: "Yaad Oren",
      title: "Global Head of Research & Innov., SAP",
      image: yaadImage,
      bio: "Yaad oversees SAP's largest development hub with 5,500+ employees across 20+ U.S. offices. He also leads a team that spearheads research in emerging technologies.",
      socials: {
        linkedin: "https://www.linkedin.com/in/yaad-oren-77a7823/",
      },
    },
  ];

  const SocialIcon = ({ platform, url }) => {
    const icons = {
      twitter: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
      linkedin: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
      github: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
        </svg>
      ),
      website: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
          />
        </svg>
      ),
    };

    return (
      <motion.a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="text-gray-400 dark:text-gray-500 hover:text-aifund hover:scale-110 hover:bg-aifund/10 transition-all duration-200 p-2 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          window.open(url, "_blank", "noopener,noreferrer");
        }}
      >
        {icons[platform]}
      </motion.a>
    );
  };

  const TiltCard = ({ children, className }) => {
    const wrapRef = useRef(null);
    const cardRef = useRef(null);

    const animationHandlers = useMemo(() => {
      let rafId = null;

      const updateCardTransform = (offsetX, offsetY, card, wrap) => {
        const width = card.clientWidth;
        const height = card.clientHeight;

        const percentX = clamp((100 / width) * offsetX);
        const percentY = clamp((100 / height) * offsetY);

        const centerX = percentX - 50;
        const centerY = percentY - 50;

        const properties = {
          "--pointer-x": `${percentX}%`,
          "--pointer-y": `${percentY}%`,
          "--background-x": `${adjust(percentX, 0, 100, 35, 65)}%`,
          "--background-y": `${adjust(percentY, 0, 100, 35, 65)}%`,
          "--pointer-from-center": `${clamp(
            Math.hypot(percentY - 50, percentX - 50) / 50,
            0,
            1
          )}`,
          "--pointer-from-top": `${percentY / 100}`,
          "--pointer-from-left": `${percentX / 100}`,
          "--rotate-x": `${round(centerY / 5)}deg`,
          "--rotate-y": `${round(-(centerX / 4))}deg`,
        };

        Object.entries(properties).forEach(([property, value]) => {
          wrap.style.setProperty(property, value);
        });
      };

      const createSmoothAnimation = (duration, startX, startY, card, wrap) => {
        const startTime = performance.now();
        const targetX = wrap.clientWidth / 2;
        const targetY = wrap.clientHeight / 2;

        const animationLoop = (currentTime) => {
          const elapsed = currentTime - startTime;
          const progress = clamp(elapsed / duration);
          const easedProgress = easeInOutCubic(progress);

          const currentX = adjust(easedProgress, 0, 1, startX, targetX);
          const currentY = adjust(easedProgress, 0, 1, startY, targetY);

          updateCardTransform(currentX, currentY, card, wrap);

          if (progress < 1) {
            rafId = requestAnimationFrame(animationLoop);
          }
        };

        rafId = requestAnimationFrame(animationLoop);
      };

      return {
        updateCardTransform,
        createSmoothAnimation,
        cancelAnimation: () => {
          if (rafId) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        },
      };
    }, []);

    const handlePointerMove = useCallback(
      (event) => {
        const card = cardRef.current;
        const wrap = wrapRef.current;

        if (!card || !wrap || !animationHandlers) return;

        // Check if hovering over a social icon (no-tilt area)
        if (event.target.closest('[data-no-tilt="true"]')) {
          // Reset CSS custom properties to make icons clickable
          wrap.style.setProperty("--rotate-x", "0deg");
          wrap.style.setProperty("--rotate-y", "0deg");
          return;
        }

        const rect = card.getBoundingClientRect();
        animationHandlers.updateCardTransform(
          event.clientX - rect.left,
          event.clientY - rect.top,
          card,
          wrap
        );
      },
      [animationHandlers]
    );

    const handlePointerEnter = useCallback(() => {
      const card = cardRef.current;
      const wrap = wrapRef.current;

      if (!card || !wrap || !animationHandlers) return;

      animationHandlers.cancelAnimation();
      wrap.classList.add("active");
      card.classList.add("active");

      // Show shine effect
      const shineEffect = card.querySelector(".shine-effect");
      if (shineEffect) {
        shineEffect.style.opacity = "1";
      }
    }, [animationHandlers]);

    const handlePointerLeave = useCallback(
      (event) => {
        const card = cardRef.current;
        const wrap = wrapRef.current;

        if (!card || !wrap || !animationHandlers) return;

        animationHandlers.createSmoothAnimation(
          ANIMATION_CONFIG.SMOOTH_DURATION,
          event.offsetX,
          event.offsetY,
          card,
          wrap
        );
        wrap.classList.remove("active");
        card.classList.remove("active");

        // Hide shine effect
        const shineEffect = card.querySelector(".shine-effect");
        if (shineEffect) {
          shineEffect.style.opacity = "0";
        }
      },
      [animationHandlers]
    );

    useEffect(() => {
      if (!animationHandlers) return;

      const card = cardRef.current;
      const wrap = wrapRef.current;

      if (!card || !wrap) return;

      const pointerMoveHandler = handlePointerMove;
      const pointerEnterHandler = handlePointerEnter;
      const pointerLeaveHandler = handlePointerLeave;

      card.addEventListener("pointerenter", pointerEnterHandler);
      card.addEventListener("pointermove", pointerMoveHandler);
      card.addEventListener("pointerleave", pointerLeaveHandler);

      const initialX = wrap.clientWidth - ANIMATION_CONFIG.INITIAL_X_OFFSET;
      const initialY = ANIMATION_CONFIG.INITIAL_Y_OFFSET;

      animationHandlers.updateCardTransform(initialX, initialY, card, wrap);
      animationHandlers.createSmoothAnimation(
        ANIMATION_CONFIG.INITIAL_DURATION,
        initialX,
        initialY,
        card,
        wrap
      );

      return () => {
        card.removeEventListener("pointerenter", pointerEnterHandler);
        card.removeEventListener("pointermove", pointerMoveHandler);
        card.removeEventListener("pointerleave", pointerLeaveHandler);
        animationHandlers.cancelAnimation();
      };
    }, [
      animationHandlers,
      handlePointerMove,
      handlePointerEnter,
      handlePointerLeave,
    ]);

    return (
      <div
        ref={wrapRef}
        className="tilt-wrapper"
        style={{
          transformStyle: "preserve-3d",
          perspective: "1000px",
        }}
      >
        <div
          ref={cardRef}
          className={className}
          style={{
            transform:
              "rotateX(var(--rotate-x, 0)) rotateY(var(--rotate-y, 0))",
            transformStyle: "preserve-3d",
            transition: "transform 0.1s ease-out",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Light shine effect */}
          <div
            className="absolute inset-0 opacity-0 pointer-events-none transition-opacity duration-300 shine-effect"
            style={{
              background: `radial-gradient(circle at var(--pointer-x, 50%) var(--pointer-y, 50%), rgba(255, 255, 255, 0.15) 0%, transparent 50%)`,
              borderRadius: "inherit",
            }}
          />
          {children}
        </div>
      </div>
    );
  };

  return (
    <div className="py-20 bg-gray-50 dark:bg-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            Presenters & Judges
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Learn from industry leaders, researchers, and entrepreneurs who are
            shaping the future of AI.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {speakers.map((speaker, index) => (
            <motion.div
              key={speaker.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <TiltCard className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-xl dark:shadow-white/10 dark:hover:shadow-white/20 transition-all duration-300 min-h-[400px]">
                <div className="text-center flex flex-col h-full">
                  <div className="relative mb-6">
                    <img
                      src={speaker.image}
                      alt={speaker.name}
                      className="w-48 h-48 rounded-full mx-auto object-cover border-4 border-aifund/20"
                    />
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    {speaker.name}
                  </h3>

                  <p className="text-aifund font-semibold mb-3">
                    {speaker.title}
                  </p>

                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-6 leading-relaxed h-21 line-clamp-4 overflow-hidden text-left">
                    {speaker.bio}
                  </p>

                  <div
                    className="flex justify-center space-x-8"
                    data-no-tilt="true"
                  >
                    {Object.entries(speaker.socials).map(([platform, url]) => (
                      <SocialIcon
                        key={platform}
                        platform={platform}
                        url={url}
                      />
                    ))}
                  </div>
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Speakers;
